/**
 * OQ-283 · Màn đối chiếu DNS sau khi gắn lại kết nối.
 *
 * ── Vì sao màn này tồn tại ─────────────────────────────────────────────────
 *
 * Xoá kết nối rồi thêm lại thì tên miền tự nhận về, nhưng bản ghi DNS trong
 * CSDL là ảnh chụp từ trước lúc mất kết nối. Trước OQ-283, `dns_sync` sẽ âm
 * thầm áp luật *"Cloudflare thắng"* trong vòng 6 giờ và xoá mọi bản ghi chỉ có
 * trong CSDL — không một câu hỏi nào.
 *
 * Nay hệ thống quét trước, ghi lại tên miền nào lệch, và **hoãn đồng bộ** cho
 * đúng những tên miền ấy cho tới khi người dùng quyết. Màn này là chỗ quyết.
 *
 * ── Vì sao có nút "Áp bản Cloudflare cho tất cả" ──────────────────────────
 *
 * Chủ dự án có 78 tên miền. Nếu 70 cái cùng lệch thì duyệt từng cái là bất khả
 * thi, và một màn hình bất khả thi sẽ bị bỏ mặc — tệ hơn cả việc không có nó,
 * vì đồng bộ đứng im mãi.
 *
 * Chạy theo LÔ và tự gọi tiếp: mỗi tên miền là một lời gọi `listDnsRecords`,
 * làm hết 78 cái trong một request sẽ dựng lại đúng cái nghẽn OQ-282 vừa gỡ.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useT } from '../lib/i18n';
import { Icon } from '../components/Icon';
import { Modal } from '../components/Modal';
import { ApiErrorAlert } from '../components/ApiErrorAlert';
import { AsyncSection, EmptyState, SkeletonCard } from '../components/States';
import { useToast } from '../components/Toast';
import { thanhApiError, type ApiError } from '../lib/api';
import { useFormat } from '../lib/format';
import { ReimportCompareModal } from './ReimportCompareModal';

interface TomTatLech {
  same: number;
  dbOnly: number;
  cfOnly: number;
  differ: number;
}

interface TenMienLech {
  domainId: string;
  name: string;
  cfZoneId: string;
  connectionId: string;
  summary: TomTatLech;
  createdAt: string;
}

export function ReconcilePage(): JSX.Element {
  const { formatDateTime } = useFormat();
  const t = useT();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [dangMo, setDangMo] = useState<TenMienLech | null>(null);
  const [xacNhanTatCa, setXacNhanTatCa] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const danhSach = useQuery({
    queryKey: ['reconcile-pending'],
    queryFn: () => api.get<{ items: TenMienLech[] }>('/domains/reconcile/pending'),
  });

  const items = danhSach.data?.items ?? [];

  const lamMoi = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['reconcile-pending'] });
    await queryClient.invalidateQueries({ queryKey: ['domains'] });
  };

  /**
   * Áp bản Cloudflare hàng loạt — tự gọi tiếp cho tới khi hết.
   *
   * API trả `conLai` chứ không tự lặp: một request chạy vài phút sẽ chạm timeout
   * của proxy, và người dùng không thấy tiến triển nào. Vòng lặp ở đây cho họ
   * con số giảm dần sau mỗi lô.
   */
  const apTatCa = useMutation({
    mutationFn: async () => {
      let tong = 0;
      let loi = 0;
      for (;;) {
        const kq = await api.post<{ daXuLy: number; loi: number; conLai: number }>(
          '/domains/reconcile/apply-cloudflare',
        );
        tong += kq.daXuLy;
        loi += kq.loi;
        await queryClient.invalidateQueries({ queryKey: ['reconcile-pending'] });
        if (kq.conLai === 0) return { tong, loi };
      }
    },
    onSuccess: async ({ tong, loi }) => {
      setXacNhanTatCa(false);
      toast(
        loi > 0
          ? t('reconcile.appliedWithErrors')
              .replace('{n}', String(tong))
              .replace('{e}', String(loi))
          : t('reconcile.applied').replace('{n}', String(tong)),
      );
      await lamMoi();
    },
    onError: (err: unknown) => setError(thanhApiError(err)),
  });

  const boQua = useMutation({
    mutationFn: (domainId: string) => api.del(`/domains/reconcile/${domainId}`),
    onSuccess: lamMoi,
    onError: (err: unknown) => setError(thanhApiError(err)),
  });

  return (
    <div data-od-id="reconcile-page">
      <div className="page-head">
        <div className="grow">
          <h1>{t('reconcile.title')}</h1>
          <div className="sub">{t('reconcile.sub')}</div>
        </div>
        {items.length > 0 ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setXacNhanTatCa(true)}
            disabled={apTatCa.isPending}
            data-od-id="reconcile-apply-all"
          >
            <Icon name="cloud" size={16} />
            {apTatCa.isPending ? t('common.processing') : t('reconcile.applyAll')}
          </button>
        ) : null}
      </div>

      <ApiErrorAlert error={error} />

      <div className="card">
        <div className="card-body">
          <div className="scope-note">
            <Icon name="info" size={17} />
            <span>{t('reconcile.note')}</span>
          </div>

          <AsyncSection
            loading={danhSach.isLoading}
            error={danhSach.error}
            data={items}
            onRetry={() => void danhSach.refetch()}
            skeleton={<SkeletonCard rows={4} />}
            empty={
              <EmptyState
                icon="check"
                title={t('reconcile.emptyTitle')}
                body={t('reconcile.emptyHint')}
              />
            }
          >
            {(ds) => (
              <div className="table-wrap">
                <table className="tbl" data-od-id="reconcile-table">
                  <thead>
                    <tr>
                      <th scope="col">{t('reconcile.colDomain')}</th>
                      <th scope="col" className="right">
                        {t('reconcile.colDbOnly')}
                      </th>
                      <th scope="col" className="right">
                        {t('reconcile.colCfOnly')}
                      </th>
                      <th scope="col" className="right">
                        {t('reconcile.colDiffer')}
                      </th>
                      <th scope="col">{t('reconcile.colSince')}</th>
                      <th scope="col" className="right">
                        {t('reconcile.colActions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {ds.map((m) => (
                      <tr key={m.domainId}>
                        <td>
                          <Link to={`/domains/${m.domainId}`} className="ten-bam-duoc">
                            <b>{m.name}</b>
                          </Link>
                        </td>
                        {/*
                        Ba con số nói ba chuyện khác nhau, nên KHÔNG gộp thành
                        một "số khác biệt": `chỉ CSDL` là thứ sắp bị xoá nếu
                        không làm gì — đó mới là con số người dùng cần thấy.
                      */}
                        <td className="right num">{m.summary.dbOnly || '—'}</td>
                        <td className="right num">{m.summary.cfOnly || '—'}</td>
                        <td className="right num">{m.summary.differ || '—'}</td>
                        <td className="muted">{formatDateTime(m.createdAt)}</td>
                        <td className="right">
                          <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={() => setDangMo(m)}
                              data-od-id="reconcile-compare"
                            >
                              {t('reconcile.compare')}
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={() => boQua.mutate(m.domainId)}
                              disabled={boQua.isPending}
                              data-od-id="reconcile-skip"
                            >
                              {t('reconcile.skip')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </AsyncSection>
        </div>
      </div>

      {dangMo ? (
        <ReimportCompareModal
          connectionId={dangMo.connectionId}
          zoneId={dangMo.cfZoneId}
          zoneName={dangMo.name}
          onClose={() => setDangMo(null)}
          onDone={async () => {
            setDangMo(null);
            await lamMoi();
          }}
        />
      ) : null}

      {xacNhanTatCa ? (
        <Modal open title={t('reconcile.applyAll')} onClose={() => setXacNhanTatCa(false)}>
          <div className="form-alert bad" role="alert">
            <Icon name="warn" size={16} />
            <span>{t('reconcile.applyAllWarn').replace('{n}', String(items.length))}</span>
          </div>
          <p className="muted">{t('reconcile.applyAllHint')}</p>
          <div className="row" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" className="btn" onClick={() => setXacNhanTatCa(false)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={apTatCa.isPending}
              onClick={() => apTatCa.mutate()}
              data-od-id="reconcile-apply-all-confirm"
            >
              {apTatCa.isPending ? t('common.processing') : t('reconcile.applyAll')}
            </button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
